import WidgetGraphique from '../shared/WidgetGraphique';
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { TrendingUp, TrendingDown } from '../../lib/icons';
import { fetchKpisCampagnes } from '../../lib/docs';
import { statsCampagneCourante } from '../../lib/dataProvider';
import { fmt } from '../shared/badges';

const INDICATEURS = [
  { k: 'chiffreAffaires', label: 'Chiffre d’affaires', unite: 'TND', dec: 3 },
  { k: 'volumeOlivesKg', label: 'Olives réceptionnées', unite: 'kg', dec: 1 },
  { k: 'volumeHuileKg', label: 'Huile produite', unite: 'kg', dec: 1 },
  { k: 'rendementMoyen', label: 'Rendement moyen', unite: '%', dec: 2 },
  { k: 'achatsOlivesTnd', label: 'Achats d’olives', unite: 'TND', dec: 3 },
  { k: 'chargesTotal', label: 'Charges', unite: 'TND', dec: 3 },
  { k: 'resultatNet', label: 'Résultat net', unite: 'TND', dec: 3 },
];

// Comparatif N vs N-1 : chiffres ERP de la campagne en cours face aux KPI
// extraits des documents du coffre par l'analyse profonde (Deep Scan).
export const ComparatifCampagnes = () => {
  const [courante, setCourante] = useState(null);
  const [histo, setHisto] = useState([]);
  const [choix, setChoix] = useState('');
  const [indicateur, setIndicateur] = useState('chiffreAffaires');

  useEffect(() => {
    statsCampagneCourante().then(setCourante);
    fetchKpisCampagnes().then((k) => {
      setHisto(k);
      setChoix(k[0]?.id ?? '');
    });
  }, []);

  if (!courante) return null;
  const ref = histo.find((h) => h.id === choix) ?? null;

  const metrique = INDICATEURS.find(i => i.k === indicateur);
  const valeur = v => v == null || v === '' || !Number.isFinite(Number(v)) ? null : Number(v);
  const donneesGraphique = ref ? [
    { label: String(ref.campagne) + ' — historique', valeur: valeur(ref[indicateur]) },
    { label: String(courante.campagne) + ' — ERP', valeur: valeur(courante[indicateur]) },
  ] : [];

  return (
    <section data-testid="comparatif-campagnes" className="border border-slate-200 border-l-2 border-l-amber-600 bg-white">
      <div className="flex flex-col gap-2 border-b border-slate-200 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <p className="font-heading text-base font-bold tracking-tight text-slate-900">
            Comparatif de campagnes — {courante.campagne} vs historique
          </p>
          <p className="text-xs text-slate-500">
            Chiffres de l’ERP confrontés aux indicateurs extraits de vos documents par l’analyse profonde du coffre
          </p>
        </div>
        {histo.length > 0 ? (
          <select
            data-testid="comparatif-choix"
            value={choix}
            onChange={(e) => setChoix(e.target.value)}
            className="h-10 border border-slate-300 bg-white px-2 text-sm"
          >
            {histo.map((h) => (
              <option key={h.id} value={h.id}>{`Campagne ${h.campagne} — ${h.documentNom}`}</option>
            ))}
          </select>
        ) : (
          <Link
            to="/coffre"
            data-testid="comparatif-vide"
            className="border border-slate-300 px-3 py-2 text-xs font-bold text-slate-700 hover:bg-slate-900 hover:text-white"
          >
            Aucun historique : lancez une analyse profonde dans le Centre Documentaire
          </Link>
        )}
      </div>

      <div className="grid grid-cols-1 gap-px bg-slate-200 sm:grid-cols-2 lg:grid-cols-4">
        {INDICATEURS.map(({ k, label, unite, dec }) => {
          const n = Number(courante[k]) || 0;
          const p = ref ? Number(ref[k]) || 0 : 0;
          const variation = p !== 0 ? ((n - p) / Math.abs(p)) * 100 : null;
          const hausse = variation !== null && variation > 0;
          return (
            <div key={k} data-testid={`comparatif-${k}`} className="bg-white px-4 py-3">
              <p className="text-[11px] font-bold uppercase tracking-[0.1em] text-slate-500">{label}</p>
              <p className="tabular font-data text-xl font-medium text-slate-900">
                {fmt(n, dec)} <span className="text-xs text-slate-500">{unite}</span>
              </p>
              <p className="tabular font-data text-[11px] text-slate-500">
                {ref ? `${ref.campagne} : ${fmt(p, dec)} ${unite}` : 'aucune référence'}
              </p>
              {variation !== null && (
                <p
                  data-testid={`variation-${k}`}
                  className={`mt-1 inline-flex items-center gap-1 font-data text-xs font-bold ${hausse ? 'text-emerald-700' : 'text-red-700'}`}
                >
                  {hausse ? <TrendingUp size={12} /> : <TrendingDown size={12} />}
                  {hausse ? '+' : ''}{fmt(variation, 1)} %
                </p>
              )}
            </div>
          );
        })}
      </div>

      <div className="space-y-3 border-t border-slate-200 p-4">
        <label className="block text-sm">Indicateur à comparer
          <select data-testid="comparatif-indicateur" className="ml-3 min-h-11 max-w-full border border-slate-300 bg-white px-2" value={indicateur} onChange={e => setIndicateur(e.target.value)}>
            {INDICATEURS.map(i => <option key={i.k} value={i.k}>{`${i.label} (${i.unite})`}</option>)}
          </select>
        </label>
        <WidgetGraphique type="barres" titre={'Comparatif de campagnes — ' + metrique.label} unite={metrique.unite}
          donnees={donneesGraphique} testid="graph-campagnes"
          description="Même indicateur que les chiffres ci-dessus, sans nouvel appel. Campagne courante potentiellement incomplète ; historique issu des documents, à vérifier. Les données absentes restent indisponibles." />
      </div>

      {ref?.commentaire && (
        <p data-testid="comparatif-commentaire" className="border-t border-slate-200 px-4 py-2 text-xs text-slate-600">
          Lecture IA du document {ref.documentNom} : {ref.commentaire}
        </p>
      )}
    </section>
  );
};
